import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';
import {
  AttachmentObject,
  AttachmentType,
  BaseDaoService,
  REF_MODEL_STORE,
  RefModelStoreService,
  SnackBarService
} from '@q9elements/ui-kit/common';
import {endsWith, findIndex, forEach, isArray, remove} from 'lodash';
import {Observable, of} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {AttachmentObjectType} from '../../models/rme/attachment-object.interface';
import {SfApiService} from '../sf-api.service';

@Injectable({
  providedIn: 'root'
})
export class AttachmentsService extends BaseDaoService {
  constructor(
    public http: HttpClient,
    private rmeStore: RefModelStoreService,
    private snackBar: SnackBarService,
    private translate: TranslateService,
    private sfService: SfApiService
  ) {
    super();
  }

  public fetchAttachments(refModelId: string, nodeId: string): Observable<any> {
    const prevNodeId = this.rmeStore.selectedNodeAttachments?.nodeId;
    const attachmentsObj = Object.assign({}, {loading: true, nodeId: prevNodeId});
    this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE_ATTACHMENTS, attachmentsObj);

    const url = `${this.apiUrl}/refmodel/${refModelId}/node/${nodeId}/attachments`;
    return this.http.get(url).pipe(
      catchError(error => {
        this.snackBar.open(this.translate.instant('NOTIFICATIONS.FETCH_ATTACHMENT_FAILED'));
        console.error('Error loading the list of attachments', error);
        this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE_ATTACHMENTS, {
          loading: false,
          nodeId: attachmentsObj.nodeId
        });
        return of();
      }),
      tap(next => {
        this.setAttachmentsToStore(next, nodeId);
        return next;
      })
    );
  }

  private setAttachmentsToStore(res, nodeId: string): void {
    const attachmentsObj = Object.assign({}, res, {loading: false, nodeId});
    this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE_ATTACHMENTS, attachmentsObj);
  }

  public getLinksNumber(attachmentsObject: AttachmentObject): number {
    const attachmentsProp = [
      AttachmentObjectType.Attachments,
      AttachmentObjectType.GeneralAttachments,
      AttachmentObjectType.RequirementAttachments,
      AttachmentObjectType.StoryAttachments
    ];
    let number = 0;
    if (attachmentsObject) {
      attachmentsProp.forEach((prop: string) => {
        number += (attachmentsObject[prop] && attachmentsObject[prop].length) || 0;
      });
    }
    return number;
  }

  private reorderAttachment(
    refModelId: string,
    nodeId: string,
    type: AttachmentType,
    updateObj: any
  ): Observable<any> {
    let url = `${this.apiUrl}/refmodel/${refModelId}/node/${nodeId}/`;
    switch (type) {
      case AttachmentType.ACTIVITY:
        url += 'armc';
        break;
      case AttachmentType.REQUIREMENT:
        url += 'link/requirement';
        break;
      case AttachmentType.STORY:
        url += 'link/story';
        break;
      default:
        url += 'attachment';
    }
    /** response type is 'text' because we need to fix angular issue when no json data in response */
    return this.http.put(url, updateObj, {responseType: 'text'}).pipe(
      tap(next => {
        this.reorderAttachmentsInStore(updateObj, type);
        return next;
      })
    );
  }

  private updateAttachment(
    refModelId: string,
    nodeId: string,
    updateObj: any,
    type: AttachmentType
  ): Observable<any> {
    let url = `${this.apiUrl}/refmodel/${refModelId}/node/${nodeId}/`;
    switch (type) {
      case 'activity':
        url += 'armc';
        break;
      default:
        url += 'attachment';
    }
    return this.http.put(url, updateObj, {responseType: 'text'}).pipe(
      tap(next => {
        this.updateAttachmentInStore(type, updateObj);
        return next;
      })
    );
  }

  private reorderAttachmentsInStore(updateObj: any, type: AttachmentType): void {
    const selectedNodeAttachments = Object.assign({}, this.rmeStore.selectedNodeAttachments);
    const attachmentsPath = this.getAttachmentsPath(type);

    const reorderedAttachmentArr = remove(
      selectedNodeAttachments[attachmentsPath],
      (attachment: any) => {
        return attachment._id === updateObj._id;
      }
    );
    selectedNodeAttachments[attachmentsPath].splice(
      updateObj.position,
      0,
      reorderedAttachmentArr[0]
    );
    this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE_ATTACHMENTS, selectedNodeAttachments);
  }

  private removeAttachment(refModelId: string, nodeId: string, updateObj: any): Observable<any> {
    let url = `${this.apiUrl}/refmodel/${refModelId}/node/${nodeId}/`;
    switch (updateObj.type) {
      case 'activity':
        url += `armc?id=${updateObj.attachmentId}`;
        break;
      case 'requirement':
        url += `link/requirement/${updateObj.attachmentId}`;
        break;
      case 'story':
        url += `link/story/${updateObj.attachmentId}`;
        break;
      default:
        url += `attachment?id=${updateObj.attachmentId}`;
    }

    return this.http.delete(url, {responseType: 'text'}).pipe(
      tap(next => {
        this.removeAttachmentInStore(updateObj.type, updateObj.attachmentId);
        return next;
      })
    );
  }

  private removeAttachmentInStore(type: AttachmentType, attachmentId: string): void {
    const selectedNodeAttachments = Object.assign({}, this.rmeStore.selectedNodeAttachments);
    const attachmentsPath = this.getAttachmentsPath(type);
    remove(selectedNodeAttachments[attachmentsPath], (attachment: any) => {
      return attachment._id === attachmentId;
    });
    this.updateAttachmentsAndLinks(selectedNodeAttachments);
    if (endsWith(this.rmeStore.selectedNode.parentPath, '.fields')) {
      this.sfService.checkAndUpdateImpact(this.rmeStore.selectedNode);
    }
  }

  public pushAttachmentInStore(type: AttachmentType, data: any) {
    const attachmentsPath = this.getAttachmentsPath(type);
    const selectedNodeAttachments = Object.assign({}, this.rmeStore.selectedNodeAttachments);
    let newAttachmentData;
    if (isArray(data)) {
      newAttachmentData = data.slice();
      selectedNodeAttachments[attachmentsPath].push(...newAttachmentData);
    } else {
      newAttachmentData = Object.assign({}, data);
      selectedNodeAttachments[attachmentsPath].push(newAttachmentData);
    }
    this.updateAttachmentsAndLinks(selectedNodeAttachments);
    if (endsWith(this.rmeStore.selectedNode.parentPath, '.fields')) {
      this.sfService.checkAndUpdateImpact(this.rmeStore.selectedNode);
    }
  }

  private updateAttachmentInStore(type: AttachmentType, updateObj: any): void {
    const attachmentsPath = this.getAttachmentsPath(type);
    const selectedNodeAttachments = Object.assign({}, this.rmeStore.selectedNodeAttachments);
    const index = findIndex(
      selectedNodeAttachments[attachmentsPath],
      (item: any) => item._id === updateObj._id
    );
    if (index > -1) {
      selectedNodeAttachments[attachmentsPath][index] = Object.assign(
        {},
        selectedNodeAttachments[attachmentsPath][index],
        updateObj
      );
    }
    this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE_ATTACHMENTS, selectedNodeAttachments);
  }

  private getAttachmentsPath(type: AttachmentType): string {
    switch (type) {
      case AttachmentType.ACTIVITY:
        return AttachmentObjectType.Attachments;
      case AttachmentType.REQUIREMENT:
        return AttachmentObjectType.RequirementAttachments;
      case AttachmentType.STORY:
        return AttachmentObjectType.StoryAttachments;
      default:
        return AttachmentObjectType.GeneralAttachments;
    }
  }

  private updateAttachmentsAndLinks(attachments: any) {
    let linksCount = 0;
    forEach(attachments, value => {
      if (isArray(value)) {
        linksCount += value.length;
      }
    });
    this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE_ATTACHMENTS, attachments);
  }

  public getGroups(groupsUrl): Observable<any> {
    const params = new HttpParams()
      .set('skip', '0')
      .set('sortBy', 'name')
      .set('simpleList', 'true');

    return this.http.get(groupsUrl, {params});
  }
}
